import { useEffect, useRef } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useLocationParams } from 'hooks';
import { collectUniqueAdSetFlows } from '../utils/adSetFlowCache';
import { restoreSelectedAdSetsFromUrl, toAdSetSelectionEntries } from '../utils/adSetUrlSync';
import type { AdSetEntry } from '../api';

/**
 * Keeps the ad set selection (+ ABO budget/duplicate status/start
 * time/duplicate count/user-assigned flow per ad set) in sync with the URL.
 * Restoring from the URL has to wait for `rows` since ad sets are fetched
 * asynchronously (and depend on the selected campaigns) — unlike the name
 * filter, this can't happen at form-default time.
 *
 * Unique flow names from the current selection are also written to the
 * `flow` query param as a list of strings (the same param Hypothesis
 * Testing uses for its creatives filter). Multi-flow rows (`"flowA,flowB"`)
 * are split so `?flow=` always holds individual names.
 */
const useAdSetsUrlSync = (
  rows: AdSetEntry[] | undefined,
  isPending: boolean,
  selectedAdSetsRows: AdSetEntry[] | undefined,
) => {
  const { control, setValue } = useFormContext();
  const selectedAdSetsBudgets = useWatch({ control, name: 'selectedAdSetsBudgets' }) as
    | Record<string, string>
    | undefined;
  const selectedAdSetsStartTimes = useWatch({ control, name: 'selectedAdSetsStartTimes' }) as
    | Record<string, string>
    | undefined;
  const selectedAdSetsDuplicatesCount = useWatch({ control, name: 'selectedAdSetsDuplicatesCount' }) as
    | Record<string, string>
    | undefined;

  const {
    selectedAdSets: selectedAdSetsParam,
    setSelectedAdSets: setSelectedAdSetsParam,
    setHypothesisFlow: setFlowParam,
  } = useLocationParams();

  const hasRestoredFromUrl = useRef(false);
  // Set right before a restore applies `selectedAdSets`/budgets/start
  // times/duplicate counts/flows. The write-back effect below runs in the same
  // commit, before RHF's `setValue` has re-rendered this component with the
  // restored values, so without this it would immediately mirror the still
  // stale (empty) selection back to the URL and wipe out the param it just
  // restored from.
  const skipNextWriteBack = useRef(false);
  const restoreTimeout = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  // Cleared only on unmount — a dependency change of the restore effect
  // below must NOT cancel an already-scheduled restore (see comment there).
  useEffect(() => {
    return () => {
      clearTimeout(restoreTimeout.current);
    };
  }, []);

  // Runs once, right when the ad sets query first settles (loading ->
  // loaded, even to an empty list), and only if the form doesn't already
  // have a selection. Gating on `isPending` — not just `rows` — matters:
  // while the query is still loading, `rows` is `undefined` and any
  // write-back below would otherwise clear the `ad_sets` URL param before
  // it's ever read.
  useEffect(() => {
    if (hasRestoredFromUrl.current || isPending) return;
    hasRestoredFromUrl.current = true;

    if (!selectedAdSetsParam.length || selectedAdSetsRows?.length) return;

    const restored = restoreSelectedAdSetsFromUrl(selectedAdSetsParam, rows ?? []);
    if (!restored.selectedAdSets.length) return;

    skipNextWriteBack.current = true;
    // Deferred to the next tick: grid cells (e.g. the duplicate-count field)
    // read `watch('selectedAdSets')`/`watch('selectedAdSetsDuplicatesCount')`
    // and subscribe to changes from their own mount effect. If this restore
    // applies `setValue` synchronously, in the same commit as those cells
    // mounting, a cell whose subscribing effect hasn't run yet can miss the
    // update entirely and never re-render with the restored value — even
    // though the form's underlying data is correct. Pushing the writes to a
    // macrotask guarantees every watcher has already subscribed.
    restoreTimeout.current = setTimeout(() => {
      setValue('selectedAdSets', restored.selectedAdSets, { shouldValidate: true });
      setValue('selectedAdSetsBudgets', restored.selectedAdSetsBudgets, {
        shouldValidate: true,
      });
      setValue('selectedAdSetsStartTimes', restored.selectedAdSetsStartTimes, {
        shouldValidate: true,
      });
      setValue('selectedAdSetsDuplicatesCount', restored.selectedAdSetsDuplicatesCount, {
        shouldValidate: true,
      });
      setValue('selectedAdSetsFlows', restored.selectedAdSetsFlows, {
        shouldValidate: true,
      });
    }, 0);
  }, [isPending, rows, selectedAdSetsParam, selectedAdSetsRows, setValue]);

  useEffect(() => {
    if (!hasRestoredFromUrl.current) return;

    if (skipNextWriteBack.current) {
      skipNextWriteBack.current = false;
      return;
    }

    setSelectedAdSetsParam(
      toAdSetSelectionEntries(
        selectedAdSetsRows ?? [],
        selectedAdSetsBudgets,
        selectedAdSetsStartTimes,
        selectedAdSetsDuplicatesCount,
      ),
    );

    // Only write `flow` once a selection exists so landing on this step
    // with nothing checked doesn't wipe a Hypothesis Testing filter that
    // already occupies the same param.
    if (selectedAdSetsRows?.length) {
      setFlowParam(collectUniqueAdSetFlows(selectedAdSetsRows));
    }
  }, [
    selectedAdSetsRows,
    selectedAdSetsBudgets,
    selectedAdSetsStartTimes,
    selectedAdSetsDuplicatesCount,
    setSelectedAdSetsParam,
    setFlowParam,
  ]);
};

/** Mirrors the "search by ad set name" chip filter to the URL. */
export const useAdSetNameFilterUrlSync = (filterByName: string[]) => {
  const { setFilterAdSetsByName } = useLocationParams();

  useEffect(() => {
    setFilterAdSetsByName(filterByName ?? []);
  }, [filterByName, setFilterAdSetsByName]);
};

export default useAdSetsUrlSync;
